// ═══════════════════════════════════════════════════════════════════════════════
// Pairing Status Page
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { ApiClientError, authApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../components/ui/ToastProvider';
import { MainMenuButton } from '../../components/MainMenuButton';
import { NotificationBell } from '../../components/NotificationBell';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { AppButton } from '../../components/ui/AppButton';
import { MainAppShell } from '../../components/layout/MainAppShell';
import {
  PairingCopyCheckIcon,
  PairingCopyClipboardIcon,
} from '../../components/pairing/PairingCopyIcons';
import { PairingInviteQrCode } from '../../components/pairing/PairingInviteQrCode';
import { buildPairInviteUrl } from '../../lib/pairInviteSession';
import { PLAY_BANNER_ABSOLUTE_TOP_4_CLASS } from '../../lib/layoutChrome';
import {
  formatPairingExpiryDurationLabel,
  isPairingCodeNotExpired,
} from '../../lib/pairingCodeExpiry';
import { PAIRING_CODE_EXPIRY_MINUTES, USER_ROLE } from '@fdh/shared';
import type { ActivePairingCodeResponse, PairingStatusResponse } from '@fdh/shared';

const ACTIVE_CODE_POLL_MS = 60_000;

const hasConsented = (consentedAt: string | undefined): boolean =>
  Boolean(consentedAt && consentedAt.trim() !== '');

export const PairingStatusPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser, updateUser } = useAuth();
  const { t } = useLanguage();
  const { showSuccessToast } = useToast();
  const [status, setStatus] = useState<PairingStatusResponse | null>(null);
  const [activeCode, setActiveCode] = useState<ActivePairingCodeResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUnpairing, setIsUnpairing] = useState(false);
  const [isUnpairDialogOpen, setIsUnpairDialogOpen] = useState(false);
  const [isResendBusy, setIsResendBusy] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [error, setError] = useState('');
  const [codeCopied, setCodeCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const fetchStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get<PairingStatusResponse>('/pairing/status');
      if (response.success && response.data) {
        setStatus(response.data);
        if (!response.data.isPaired && user?.role === USER_ROLE.EMPLOYER) {
          const codeResponse = await api.get<ActivePairingCodeResponse>('/pairing/active-code');
          if (codeResponse.success && codeResponse.data) {
            setActiveCode(codeResponse.data);
          }
        } else {
          setActiveCode(null);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('pairing.status.errorGeneric'));
    } finally {
      setIsLoading(false);
    }
  }, [t, user?.role]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (isLoading || status?.isPaired || user?.role !== USER_ROLE.EMPLOYER) {
      return;
    }
    const timer = window.setInterval(() => {
      void fetchStatus();
    }, ACTIVE_CODE_POLL_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [fetchStatus, isLoading, status?.isPaired, user?.role]);

  const handleCopyActiveCode = useCallback(async () => {
    if (!activeCode?.code) {
      return;
    }
    try {
      await navigator.clipboard.writeText(activeCode.code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch {
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  }, [activeCode?.code]);

  const handleCopyActiveInviteLink = useCallback(async () => {
    if (!activeCode?.code) {
      return;
    }
    const url = buildPairInviteUrl(activeCode.code);
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  }, [activeCode?.code]);

  const handleNavigateToPairing = useCallback(() => {
    if (user?.role === 'employer') {
      navigate('/pairing/generate-code');
    } else {
      navigate('/pairing/join');
    }
  }, [user?.role, navigate]);

  const handleOpenUnpairDialog = useCallback(() => {
    if (user?.role !== 'employer') {
      return;
    }
    setIsUnpairDialogOpen(true);
  }, [user?.role]);

  const handleCloseUnpairDialog = useCallback(() => {
    if (isUnpairing) {
      return;
    }
    setIsUnpairDialogOpen(false);
  }, [isUnpairing]);

  const handleUnpair = useCallback(async () => {
    if (user?.role !== 'employer') {
      return;
    }

    setIsUnpairing(true);
    setError('');
    try {
      await api.delete<{ unpaired: true; pairingId: string }>('/pairing/unpair');
      const refreshedUser = await refreshUser();
      const nextUser = refreshedUser ?? user;
      if (nextUser) {
        updateUser({ ...nextUser, pairedAt: '' });
      }
      setIsUnpairDialogOpen(false);
      showSuccessToast(t('toast.pairing.unpairSuccess'));
      navigate('/pairing/generate-code', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('pairing.status.unpairError'));
    } finally {
      setIsUnpairing(false);
    }
  }, [navigate, refreshUser, showSuccessToast, t, updateUser, user]);

  const handleResendVerification = useCallback(async () => {
    setIsResendBusy(true);
    setError('');
    setResendMessage('');
    try {
      const response = await authApi.resendVerification();
      if (!response.success) {
        throw new Error(response.error ?? t('settings.resendVerificationError'));
      }
      setResendMessage(t('settings.resendVerificationSuccess'));
    } catch (err) {
      if (
        err instanceof ApiClientError &&
        err.status === 429 &&
        err.code === 'RESEND_VERIFICATION_COOLDOWN'
      ) {
        setError(t('settings.resendVerificationCooldown'));
        return;
      }
      setError(err instanceof Error ? err.message : t('settings.resendVerificationError'));
    } finally {
      setIsResendBusy(false);
    }
  }, [t]);

  const isPaired = Boolean(status?.isPaired);
  const employerMayUseShell =
    user?.role === USER_ROLE.EMPLOYER && Boolean(user?.emailVerified) && hasConsented(user?.consentedAt);
  const showMainShell = isPaired || employerMayUseShell;
  const showEmailHint = Boolean(user && !user.emailVerified);
  const showConsentHint = Boolean(user && !hasConsented(user.consentedAt));
  const hasActiveValidCode =
    user?.role === USER_ROLE.EMPLOYER &&
    Boolean(activeCode?.code) &&
    isPairingCodeNotExpired(activeCode?.expiresAt);

  if (isLoading) {
    const loadingView = (
      <div
        className={`relative flex items-center justify-center bg-gray-50 px-4 dark:bg-gray-950 ${showMainShell ? 'min-h-full' : 'min-h-screen'}`}
        role="status"
        aria-label={t('pairing.status.loadingAria')}
      >
        {showMainShell ? null : (
          <div className={`absolute right-4 z-10 flex items-center gap-2 ${PLAY_BANNER_ABSOLUTE_TOP_4_CLASS}`}>
            <MainMenuButton />
            <NotificationBell />
          </div>
        )}
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400" />
      </div>
    );
    return showMainShell ? <MainAppShell>{loadingView}</MainAppShell> : loadingView;
  }

  const resolvePartnerRoleLabel = (role: string | undefined): string => {
    if (role === 'helper') return t('role.helper');
    if (role === 'employer') return t('role.employer');
    return role ?? '';
  };
  const partnerRoleLabel = resolvePartnerRoleLabel(status?.partner?.role);

  const content = (
    <div className={`relative flex items-center justify-center bg-gray-50 px-4 dark:bg-gray-950 ${showMainShell ? 'min-h-full py-6' : 'min-h-screen'}`}>
      {showMainShell ? null : (
        <div className={`absolute right-4 z-10 flex items-center gap-2 ${PLAY_BANNER_ABSOLUTE_TOP_4_CLASS}`}>
          <MainMenuButton />
          <NotificationBell />
        </div>
      )}
      <div className={`w-full ${showMainShell ? 'max-w-lg' : 'max-w-md'}`}>
        {showEmailHint || showConsentHint ? (
          <div
            className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4"
            role="status"
            aria-label={
              showEmailHint
                ? t('pairing.status.emailUnverifiedHint')
                : t('pairing.status.consentRequiredHint')
            }
          >
            {showEmailHint ? (
              <p className="text-sm text-amber-900">{t('pairing.status.emailUnverifiedHint')}</p>
            ) : null}
            {showConsentHint ? (
              <p className={`text-sm text-amber-900 ${showEmailHint ? 'mt-2' : ''}`}>
                {t('pairing.status.consentRequiredHint')}
              </p>
            ) : null}
            {showEmailHint ? (
              <button
                type="button"
                disabled={isResendBusy}
                onClick={() => void handleResendVerification()}
                className="mt-3 w-full min-h-11 rounded-xl bg-amber-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 disabled:cursor-not-allowed disabled:opacity-60"
                aria-label={t('settings.resendVerification')}
                tabIndex={0}
              >
                {isResendBusy
                  ? t('settings.resendVerificationSending')
                  : t('settings.resendVerification')}
              </button>
            ) : null}
            {resendMessage ? (
              <p className="mt-2 text-sm text-emerald-800" role="status">
                {resendMessage}
              </p>
            ) : null}
          </div>
        ) : null}
        <div className="rounded-2xl bg-white p-8 shadow-lg dark:bg-gray-900 dark:shadow-gray-950/50">
          {error && (
            <div
              className="mb-6 rounded-lg bg-red-50 p-4 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300"
              role="alert"
            >
              {error}
            </div>
          )}

          {status?.isPaired ? (
            <>
              <div className="mb-6 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/40">
                  <svg
                    className="h-8 w-8 text-green-600 dark:text-green-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h1
                  className="text-2xl font-bold text-gray-900 dark:text-white"
                  role="heading"
                  aria-level={1}
                >
                  {t('pairing.status.activeTitle')}
                </h1>
              </div>

              {status.partner && (
                <div className="rounded-xl bg-gray-50 p-5 dark:bg-gray-800/80">
                  <p className="mb-3 text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                    {t('pairing.status.yourPartner')}
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/50">
                      <span className="text-xl font-bold text-primary-700 dark:text-primary-200">
                        {status.partner.nickname.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {status.partner.nickname}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{partnerRoleLabel}</p>
                    </div>
                  </div>
                  {status.pairing?.pairedAt && (
                    <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
                      {t('pairing.status.pairedSince', { datetime: status.pairing.pairedAt })}
                    </p>
                  )}
                </div>
              )}

              {user?.role === 'employer' && (
                <button
                  type="button"
                  onClick={handleOpenUnpairDialog}
                  disabled={isUnpairing}
                  className="mt-4 w-full rounded-lg bg-red-600 px-6 py-4 text-base font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:outline-none"
                  tabIndex={0}
                  aria-label={t('pairing.status.unpairAria')}
                >
                  {isUnpairing
                    ? t('pairing.status.unpairing')
                    : t('pairing.status.unpairButton')}
                </button>
              )}
            </>
          ) : (
            <>
              <div className="mb-6 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                  <svg
                    className="h-8 w-8 text-gray-400 dark:text-gray-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    />
                  </svg>
                </div>
                <h1
                  className="text-2xl font-bold text-gray-900 dark:text-white"
                  role="heading"
                  aria-level={1}
                >
                  {t('pairing.status.notPairedTitle')}
                </h1>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {user?.role === 'employer'
                    ? t('pairing.status.notPairedEmployer')
                    : t('pairing.status.notPairedHelper')}
                </p>
              </div>

              {user?.role === USER_ROLE.EMPLOYER && activeCode?.code ? (
                <div className="mb-6">
                  <div className="rounded-xl border border-primary-200 bg-primary-50 p-5 text-center dark:border-primary-600 dark:bg-gray-800">
                    <p className="mb-2 text-xs font-medium tracking-wider text-primary-700 uppercase dark:text-primary-200">
                      {t('pairing.generate.codeLabel')}
                    </p>
                    <div className="flex min-w-0 items-center justify-center gap-2 px-1">
                      <p
                        className="min-w-0 font-mono text-[clamp(1.25rem,7.5vw,2.5rem)] font-bold tracking-[0.08em] text-primary-800 sm:tracking-[0.15em] dark:text-white"
                        aria-label={`Pairing code: ${activeCode.code.split('').join(' ')}`}
                      >
                        {activeCode.code}
                      </p>
                      <button
                        type="button"
                        onClick={() => void handleCopyActiveCode()}
                        className="inline-flex shrink-0 items-center justify-center rounded-lg p-2 text-primary-700 transition-colors hover:bg-primary-100 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:outline-none dark:text-primary-100 dark:hover:bg-gray-700 dark:focus:ring-offset-gray-800"
                        tabIndex={0}
                        aria-label={
                          codeCopied ? t('pairing.generate.copied') : t('pairing.generate.copyAria')
                        }
                      >
                        {codeCopied ? <PairingCopyCheckIcon /> : <PairingCopyClipboardIcon />}
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleCopyActiveInviteLink()}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-primary-300 bg-primary-50 px-4 py-3 text-sm font-medium text-primary-900 transition-colors hover:bg-primary-100 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:outline-none dark:border-primary-500 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700 dark:focus:ring-offset-gray-900"
                    tabIndex={0}
                    aria-label={t('pairing.generate.copyInviteLinkAria')}
                  >
                    {linkCopied ? (
                      <>
                        {t('pairing.generate.copied')}
                        <PairingCopyCheckIcon />
                      </>
                    ) : (
                      <>
                        {t('pairing.generate.copyInviteLink')}
                        <PairingCopyClipboardIcon />
                      </>
                    )}
                  </button>
                  <PairingInviteQrCode code={activeCode.code} />
                  {activeCode.expiresAt ? (
                    <p className="mt-3 text-center text-sm font-bold text-primary-800 dark:text-primary-300">
                      {t('pairing.generate.validUntil', {
                        datetime: activeCode.expiresAt,
                        duration: formatPairingExpiryDurationLabel(PAIRING_CODE_EXPIRY_MINUTES, t),
                      })}
                    </p>
                  ) : null}
                  <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
                    {t('pairing.generate.footer')}
                  </p>
                </div>
              ) : null}

              {!hasActiveValidCode ? (
                <AppButton
                  type="button"
                  variant="primary"
                  size="lg"
                  fullWidth
                  onClick={handleNavigateToPairing}
                  aria-label={
                    user?.role === 'employer'
                      ? t('pairing.status.generateAria')
                      : t('pairing.status.enterAria')
                  }
                  className="px-6"
                >
                  {user?.role === 'employer'
                    ? t('pairing.status.goGenerate')
                    : t('pairing.status.goEnter')}
                </AppButton>
              ) : null}
            </>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={isUnpairDialogOpen}
        title={t('pairing.status.unpairButton')}
        description={t('pairing.status.unpairConfirm')}
        confirmLabel={isUnpairing ? t('pairing.status.unpairing') : t('common.confirm')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void handleUnpair()}
        onCancel={handleCloseUnpairDialog}
        variant="danger"
      />
    </div>
  );

  return showMainShell ? <MainAppShell>{content}</MainAppShell> : content;
};
